// ✅ src/pages/VehicleV2/VehicleTransferReport.jsx
// Production listing template
// GET /api/vehicleTransferReport/meta/branches
// GET /api/vehicleTransferReport/meta/vehicleMainTypes
// GET /api/vehicleTransferReport/list

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "https://nissi-india.com/api/vehicleTransferReport";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select" || s === "0";
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const downloadCsv = (rows, filename = "vehicle-transfer-report.csv") => {
  if (!rows?.length) return toast.info("No rows to export");
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => `"${String(r?.[h] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function VehicleTransferReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [type, setType] = useState("B");
  const [branches, setBranches] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const isBusy = loadingMeta || loadingList;

  const [filters, setFilters] = useState({
    fromDate: daysAgoISO(30),
    toDate: todayISO(),
    vehicleName: "",
    vehicleRegistrationNumber: "",
    vehicleCode: "",
    fromBranchId: sessionBranchType === "HO" ? "0" : String(sessionBranchId || "0"),
    toBranchId: "0",
    vehicleMainTypeId: "0",
  });
  const [rows, setRows] = useState([]);

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage]);

  const loadMeta = async () => {
    try {
      setLoadingMeta(true);
      const [b, vt] = await Promise.all([
        axios.get(`${API_BASE}/meta/branches`, { headers: apiHeaders }),
        axios.get(`${API_BASE}/meta/vehicleMainTypes`, { headers: apiHeaders }),
      ]);
      setBranches(b?.data?.rows || []);
      setVehicleTypes(vt?.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Meta load failed");
    } finally {
      setLoadingMeta(false);
    }
  };

  const loadList = async (p = filters, t = type, silent = false) => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams({
        fromDate: toStr(p.fromDate),
        toDate: toStr(p.toDate),
        vehicleName: toStr(p.vehicleName),
        vehicleRegistrationNumber: toStr(p.vehicleRegistrationNumber),
        vehicleCode: toStr(p.vehicleCode),
        fromBranchId: isSelect(p.fromBranchId) ? "0" : toStr(p.fromBranchId),
        toBranchId: isSelect(p.toBranchId) ? "0" : toStr(p.toBranchId),
        vehicleMainTypeId: isSelect(p.vehicleMainTypeId) ? "0" : toStr(p.vehicleMainTypeId),
        type: t,
      });
      const rs = await axios.get(`${API_BASE}/list?${params.toString()}`, { headers: apiHeaders });
      const list = rs?.data?.rows || [];
      setRows(list);
      setCurrentPage(1);
      if (!silent) toast.success(`Found ${list.length} record(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "List load failed");
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadMeta();
  }, []);

  const handleChange = (e) => setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const handleSearch = () => loadList(filters, type);
  const handleViewAll = () => {
    const reset = {
      fromDate: "",
      toDate: "",
      vehicleName: "",
      vehicleRegistrationNumber: "",
      vehicleCode: "",
      fromBranchId: sessionBranchType === "HO" ? "0" : String(sessionBranchId || "0"),
      toBranchId: "0",
      vehicleMainTypeId: "0",
    };
    setFilters(reset);
    loadList(reset, type, true);
    toast.info("Showing all records");
  };
  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      fromDate: "",
      toDate: "",
      vehicleName: "",
      vehicleRegistrationNumber: "",
      vehicleCode: "",
      toBranchId: "0",
      vehicleMainTypeId: "0",
      fromBranchId: sessionBranchType === "HO" ? prev.fromBranchId : String(sessionBranchId || "0"),
    }));
    setRows([]);
    setCurrentPage(1);
    toast.info("Filters cleared");
  };

  const handleExport = () => {
    if (!rows?.length) return toast.info("No data to export");
    downloadCsv(
      rows.map((r) => ({
        sno: r.sno,
        vehicleMainType: r.vehicleMainType,
        vehicleCode: r.vehicleCode,
        vehicleRegNo: r.vehicleRegNo,
        transferFrom: r.transferFrom,
        transferTo: r.transferTo,
        fbRecDate: r.fbRecDate,
        transferDate: r.transferDate,
        transferBy: r.transferBy,
        approvedBy: r.approvedBy,
        approvedDate: r.approvedDate,
        workHr: r.workHr,
        cumHr: r.cumHr,
      }))
    );
    toast.success("Exported CSV");
  };

  const handlePrintAll = () => {
    const qs = new URLSearchParams({ ...filters, type }).toString();
    navigate(`/vehicle/VehicleTransferReport_Print?${qs}`);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">VEHICLE TRANSFER REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>Vehicle Transfer Report</h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrintAll} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}><FaPrint /> Print</button>
              <button onClick={handleExport} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaFileExport /> Export</button>
              <button onClick={loadMeta} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSearch /> {loadingList ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="flex items-center gap-6 mb-4">
                <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  <input type="radio" name="trType" value="B" checked={type === "B"} onChange={() => setType("B")} />
                  Branch
                </label>
                <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  <input type="radio" name="trType" value="L" checked={type === "L"} onChange={() => setType("L")} />
                  Location
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>From Date</label>
                  <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={fieldClass} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>To Date</label>
                  <input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={fieldClass} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Name</label>
                  <input type="text" name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={fieldClass} placeholder="Vehicle Name" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input type="text" name="vehicleRegistrationNumber" value={filters.vehicleRegistrationNumber} onChange={handleChange} className={fieldClass} placeholder="Vehicle Reg No" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>From Branch</label>
                  <select name="fromBranchId" value={filters.fromBranchId} onChange={handleChange} className={fieldClass} disabled={sessionBranchType !== "HO"}>
                    <option value="0">Select</option>
                    {branches.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>To Branch</label>
                  <select name="toBranchId" value={filters.toBranchId} onChange={handleChange} className={fieldClass}>
                    <option value="0">Select</option>
                    {branches.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select name="vehicleMainTypeId" value={filters.vehicleMainTypeId} onChange={handleChange} className={fieldClass}>
                    <option value="0">Select</option>
                    {vehicleTypes.map((t) => <option key={t.id} value={String(t.id)}>{t.name || t.code || t.id}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={fieldClass} placeholder="Vehicle Code" />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {rows.length}</div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{loadingMeta ? "Loading dropdowns..." : loadingList ? "Searching..." : "Ready"}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loadingList ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}><div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div></div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Transfer Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Try Search or View All.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">S.No</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Vehicle Code</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Reg No</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">From</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">To</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Transfer Date</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Transfer By</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Approved By</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {pagedRows.map((r, i) => (
                    <tr key={`${r.transferRowId || i}`} className={`${i % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.vehicleCode || ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.vehicleRegNo || ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.transferFrom || ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.transferTo || ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.transferDate || ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.transferBy || ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.approvedBy || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}